// ============================================================================
// client/toile-papier.ts — LA TOILE DE BANDELETTES (06/08)
// ----------------------------------------------------------------------------
// Retour de Claude-Alain sur la première version : « le visuel du piège momie
// est mauvais, on doit pouvoir le poser partout un peu comme une toile
// d'araignée. La zone piégée doit être de 2 cases (cela permet de faire un
// meilleur visuel). » D'où : un dessin qui occupe DEUX cases (96×48 à
// l'échelle du jeu), tendu entre quatre ancrages, et qui se lit aussi bien
// posé au sol qu'accroché en l'air ou en travers d'une échelle.
//
// TROIS candidats, dessinés ici pour qu'il choisisse (planche
// /pages/toile-momie.html). Le jeu en peint UN : `TOILE_CHOISIE`. Changer ce
// seul nombre bascule tout le jeu — planche et jeu partagent ce fichier, donc
// la maquette ne peut pas mentir sur le rendu final.
// ============================================================================

import { rgba } from './papier'

type Ctx = CanvasRenderingContext2D

export type ToileVariante = 0 | 1 | 2
export const TOILE_NOMS = ['La toile tendue', 'Le filet de bandelettes', 'Le rideau suspendu'] as const

/** Le dessin peint par le JEU. Changer ce nombre = changer la toile partout.
 *  06/08 : Claude-Alain a choisi **T3, le rideau suspendu** (planche /pages/toile-momie.html). */
export const TOILE_CHOISIE: ToileVariante = 2

const FIL = '#f2e4cc'      // le papier crème des bandes
const OMBRE = '#2e2214'
const ACCENT = '#c9b48d'   // le liseré qui détache la toile des fonds clairs

/**
 * Peint une toile de DEUX cases dans le rectangle (x, y, w, h) — w vaut deux
 * fois la case. `t` anime la respiration (le tick du jeu ; une valeur fixe fige,
 * c'est ce que fait la planche). `arme` : false = elle scintille encore, elle
 * n'est pas encore dangereuse pour son poseur.
 */
export function toilePapier(g: Ctx, x: number, y: number, w: number, h: number, t: number, v: ToileVariante = TOILE_CHOISIE, arme = true): void {
  const respire = Math.sin(t * 0.06) * 0.9          // la toile frémit, elle est vivante
  g.save()
  g.globalAlpha = arme ? 1 : 0.55 + 0.25 * Math.sin(t * 0.5) // pas encore armée : elle clignote
  g.lineCap = 'round'
  g.lineJoin = 'round'
  if (v === 0) toileTendue(g, x, y, w, h, respire)
  else if (v === 1) filetBandelettes(g, x, y, w, h, respire)
  else rideauSuspendu(g, x, y, w, h, respire, t)
  g.restore()
}

/* ─────────────── 0 · LA TOILE TENDUE — l'araignée classique, en papier ─────
   Deux moyeux (un par case), des rayons vers les quatre coins, et des arcs
   concentriques tendus entre les rayons. C'est la forme que tout le monde
   reconnaît en un dixième de seconde, y compris les enfants. */
function toileTendue(g: Ctx, x: number, y: number, w: number, h: number, respire: number): void {
  const k = h / 48
  // UNE SEULE toile qui remplit les deux cases : moyeu au centre, rayons jusqu'au bord de
  // l'emprise (donc plus longs à l'horizontale qu'à la verticale — une toile tendue entre deux
  // murs écartés s'étire, exactement comme dans la nature). Deux moyeux se lisaient comme DEUX
  // toiles côte à côte (vu à la maquette) : c'est le rayon elliptique qui fait l'unité.
  const cx = x + w / 2, cy = y + h * 0.5 + respire
  const RX = w * 0.5, RY = h * 0.5
  const N = 12
  const bord = (a: number, f: number): [number, number] => [cx + Math.cos(a) * RX * f, cy + Math.sin(a) * RY * f]
  // les rayons : ombre portée d'abord (le papier découpé porte toujours son ombre)
  for (const [col, ep, dx, dy] of [[rgba(OMBRE, 0.3), 3 * k, 2, 3], [FIL, 2.2 * k, 0, 0]] as [string, number, number, number][]) {
    g.strokeStyle = col; g.lineWidth = ep
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2 + 0.13
      const [bx, by] = bord(a, 1)
      g.beginPath(); g.moveTo(cx + dx, cy + dy); g.lineTo(bx + dx, by + dy); g.stroke()
    }
  }
  // les spirales : chaque brin s'affaisse un peu entre deux rayons, c'est ce qui fait « tendu »
  g.lineWidth = 1.8 * k
  for (const f of [0.3, 0.55, 0.8, 1]) {
    g.strokeStyle = f === 1 ? rgba(ACCENT, 0.95) : rgba(FIL, 0.9)
    g.beginPath()
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI * 2 + 0.13
      const [px, py] = bord(a, f)
      if (i === 0) g.moveTo(px, py)
      else {
        const am = ((i - 0.5) / N) * Math.PI * 2 + 0.13
        const [mx, my] = bord(am, f * 0.86)
        g.quadraticCurveTo(mx, my, px, py)
      }
    }
    g.stroke()
  }
  // les quatre ancrages aux coins de l'emprise : ils disent « c'est tendu ENTRE deux appuis »
  g.strokeStyle = rgba(ACCENT, 0.8); g.lineWidth = 2.2 * k
  for (const [ax, ay] of [[x + 1, y + 1], [x + w - 1, y + 1], [x + 1, y + h - 1], [x + w - 1, y + h - 1]] as [number, number][]) {
    g.beginPath(); g.moveTo(ax, ay); g.lineTo(cx + (ax - cx) * 0.72, cy + (ay - cy) * 0.72); g.stroke()
  }
}

/* ─────── 1 · LE FILET DE BANDELETTES — la momie plutôt que l'araignée ──────
   Des bandes de papier croisées en X, bords déchirés, nouées aux quatre coins.
   Moins « toile », plus « bandage » : ça dit ce que ça fait (momifier). */
function filetBandelettes(g: Ctx, x: number, y: number, w: number, h: number, respire: number): void {
  const k = h / 48
  const bande = (x1: number, y1: number, x2: number, y2: number, ep: number): void => {
    g.strokeStyle = rgba(OMBRE, 0.28); g.lineWidth = ep + 1.5 * k
    g.beginPath(); g.moveTo(x1 + 2, y1 + 3); g.lineTo(x2 + 2, y2 + 3); g.stroke()
    g.strokeStyle = FIL; g.lineWidth = ep
    g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.stroke()
    // le grain du papier : deux traits fins le long de la bande
    g.strokeStyle = rgba(ACCENT, 0.7); g.lineWidth = 1
    g.beginPath(); g.moveTo(x1, y1 - ep * 0.25); g.lineTo(x2, y2 - ep * 0.25); g.stroke()
  }
  const T = y + h * 0.08 + respire, B = y + h * 0.92 + respire
  for (let i = 0; i <= 4; i++) {
    const a = x + (i / 4) * w, b = x + ((i + 1.6) / 4) * w
    bande(a, T, Math.min(b, x + w), B, 5 * k)
    bande(Math.min(b, x + w), T, a, B, 5 * k)
  }
  // les deux traverses horizontales qui tiennent le filet
  bande(x + 2, y + h * 0.3 + respire, x + w - 2, y + h * 0.26 + respire, 4 * k)
  bande(x + 2, y + h * 0.72 + respire, x + w - 2, y + h * 0.76 + respire, 4 * k)
  // les nœuds aux quatre coins : les ancrages, ce qui dit « c'est tendu ENTRE »
  g.fillStyle = FIL
  for (const [nx, ny] of [[x + 3, T], [x + w - 3, T], [x + 3, B], [x + w - 3, B]] as [number, number][]) {
    g.beginPath(); g.arc(nx, ny, 3.4 * k, 0, Math.PI * 2); g.fill()
  }
}

/* ────────── 2 · LE RIDEAU SUSPENDU — la nappe de bandes qui pendent ────────
   Des bandes accrochées en haut qui tombent en ondulant, longueurs inégales,
   bouts effilochés. La plus « organique » des trois, et celle qui se lit le
   mieux en travers d'une échelle ou suspendue dans le vide. */
function rideauSuspendu(g: Ctx, x: number, y: number, w: number, h: number, respire: number, t: number): void {
  const k = h / 48
  // la tringle : le fil d'accroche, tendu d'un bord à l'autre
  g.strokeStyle = rgba(OMBRE, 0.3); g.lineWidth = 4 * k
  g.beginPath(); g.moveTo(x + 2, y + h * 0.1 + 3); g.lineTo(x + w + 2, y + h * 0.09 + 3); g.stroke()
  g.strokeStyle = ACCENT; g.lineWidth = 3 * k
  g.beginPath(); g.moveTo(x, y + h * 0.1); g.lineTo(x + w, y + h * 0.09); g.stroke()
  const N = 9
  for (let i = 0; i < N; i++) {
    const bx = x + (i + 0.5) * (w / N)
    const long = h * (0.5 + 0.42 * Math.abs(Math.sin(i * 1.7)))       // longueurs inégales, déterministes
    const balance = Math.sin(t * 0.05 + i * 0.8) * 2.5 * k             // chaque bande balance à son rythme
    g.strokeStyle = rgba(OMBRE, 0.26); g.lineWidth = 6 * k
    g.beginPath(); g.moveTo(bx + 2, y + h * 0.1 + 3)
    g.quadraticCurveTo(bx + balance + 2, y + h * 0.1 + long * 0.6 + 3, bx + balance * 1.8 + 2, y + h * 0.1 + long + 3)
    g.stroke()
    g.strokeStyle = FIL; g.lineWidth = 5 * k
    g.beginPath(); g.moveTo(bx, y + h * 0.1)
    g.quadraticCurveTo(bx + balance, y + h * 0.1 + long * 0.6, bx + balance * 1.8, y + h * 0.1 + long)
    g.stroke()
    // le bout effiloché : deux petites langues de papier
    g.strokeStyle = rgba(FIL, 0.85); g.lineWidth = 2 * k
    const ex = bx + balance * 1.8, ey = y + h * 0.1 + long
    g.beginPath(); g.moveTo(ex, ey); g.lineTo(ex - 2.5 * k, ey + 4 * k); g.stroke()
    g.beginPath(); g.moveTo(ex, ey); g.lineTo(ex + 2.5 * k, ey + 3 * k); g.stroke()
  }
  // deux liens horizontaux qui relient les bandes : sans eux, ce n'est qu'une frange
  g.strokeStyle = rgba(ACCENT, 0.85); g.lineWidth = 1.8 * k
  for (const f of [0.38, 0.62]) {
    g.beginPath()
    for (let i = 0; i < N; i++) {
      const bx = x + (i + 0.5) * (w / N) + Math.sin(t * 0.05 + i * 0.8) * 1.6 * k
      const py = y + h * (0.1 + f * 0.8) + respire
      if (i === 0) g.moveTo(bx, py); else g.lineTo(bx, py)
    }
    g.stroke()
  }
}
